import React, { useMemo, useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Label } from '../ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { useStudents } from '../../contexts/StudentsContext';
import { GENDER_OPTIONS, type ExportOptions } from '../../types/student';
import { describeExportScope, exportStudentsToExcel, selectStudentsForExport } from '../../utils/excel';
import { uniqueClasses } from '../../utils/stats';
interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function ExportDialog({
  open,
  onOpenChange
}: ExportDialogProps) {
  const {
    students
  } = useStudents();
  const classes = useMemo(() => uniqueClasses(students), [students]);
  const [options, setOptions] = useState<ExportOptions>({
    scope: 'all',
    className: '',
    gender: ''
  });
  const [exporting, setExporting] = useState(false);
  const selection = useMemo(() => selectStudentsForExport(students, options), [students, options]);
  const blocked = options.scope === 'class' && !options.className || options.scope === 'gender' && !options.gender;
  const handleExport = async () => {
    if (selection.length === 0) {
      toast.warning('No records match this filter.');
      return;
    }
    setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 240));
    try {
      const fileName = exportStudentsToExcel(selection, options);
      toast.success(`Exported ${selection.length} record${selection.length === 1 ? '' : 's'}`, {
        description: fileName
      });
      onOpenChange(false);
    } catch (error) {
      toast.error('Excel export failed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setExporting(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-91" data-mp="1ah6s033aww2r">
      <DialogContent className="glass-strong max-w-lg rounded-3xl" data-id="element-92" data-mp="1fhbbm1zia6x7">
        <DialogHeader data-id="element-93" data-mp="1fimf00p1hcns">
          <DialogTitle className="flex items-center gap-2 text-lg" data-id="element-94" data-mp="1feynf735n0bp">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/15 text-emerald-600" data-id="element-95" data-mp="11j4n3cgg5ja7">
              <FileSpreadsheet className="h-4 w-4" data-id="element-96" data-mp="16gwm0qfie5nw" />
            </span>
            Configure Excel export
          </DialogTitle>
          <DialogDescription data-id="element-97" data-mp="1y7sz9s5hecbp">
            The report is compiled live from your school database at the moment you download it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2" data-id="element-98" data-mp="1kebg48gpj1m7">
          <div className="space-y-2" data-id="element-99" data-mp="1peph56jw5baq">
            <Label data-id="element-100" data-mp="1pdo5azv1wkry">Export scope</Label>
            <Select value={options.scope} onValueChange={scope => setOptions({
            scope: scope as ExportOptions['scope'],
            className: '',
            gender: ''
          })} data-id="element-101" data-mp="2d5d3gwuk03gt">
              <SelectTrigger className="w-full rounded-xl" data-id="element-102" data-mp="2r9txrav4tvro">
                <SelectValue placeholder="Choose a scope" data-id="element-103" data-mp="2n906q8me80uk" />
              </SelectTrigger>
              <SelectContent data-id="element-104" data-mp="2n77aln9eoz0t">
                <SelectItem value="all" data-id="element-105" data-mp="310ya02tfb2it">All students</SelectItem>
                <SelectItem value="class" data-id="element-106" data-mp="312j7a074gzkd">Filter by class</SelectItem>
                <SelectItem value="gender" data-id="element-107" data-mp="2w2n23xmov238">Filter by gender</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {options.scope === 'class' && <div className="space-y-2" data-id="element-108" data-mp="24b7fw4p1nhdj">
              <Label data-id="element-109" data-mp="24c8sidlpkbxd">Class</Label>
              {classes.length === 0 ? <p className="rounded-xl border border-dashed border-[var(--glass-hairline)] p-3 text-sm text-muted-foreground" data-id="element-110" data-mp="2ibi7j9y6gzzr">
                  No classes recorded yet.
                </p> : <Select value={options.className} onValueChange={className => setOptions({
            ...options,
            className
          })} data-id="element-111" data-mp="3tn99fa5amxe1">
                  <SelectTrigger className="w-full rounded-xl" data-id="element-112" data-mp="3osddw00ulrw6">
                    <SelectValue placeholder="Select a class" data-id="element-113" data-mp="1yagkmxlu1dvt" />
                  </SelectTrigger>
                  <SelectContent data-id="element-114" data-mp="238rxlh7fc4i9">
                    {classes.map(className => <SelectItem key={className} value={className} data-id="element-115" data-mp="2860c97td5qer">
                        {className}
                      </SelectItem>)}
                  </SelectContent>
                </Select>}
            </div>}

          {options.scope === 'gender' && <div className="space-y-2" data-id="element-116" data-mp="2m5jjlyecoxq0">
              <Label data-id="element-117" data-mp="2m4i0r7r3f6xq">Gender</Label>
              <Select value={options.gender} onValueChange={gender => setOptions({
            ...options,
            gender: gender as ExportOptions['gender']
          })} data-id="element-118" data-mp="2qxb3xr6wy77p">
                <SelectTrigger className="w-full rounded-xl" data-id="element-119" data-mp="2zyrbzsa99x8r">
                  <SelectValue placeholder="Select a gender" data-id="element-120" data-mp="2zwelurucggkm" />
                </SelectTrigger>
                <SelectContent data-id="element-121" data-mp="34v9hiudetzm5">
                  {GENDER_OPTIONS.map(gender => <SelectItem key={gender} value={gender} data-id="element-122" data-mp="tbaa2zwtz0ui">
                      {gender}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>}

          <div className="flex items-center justify-between rounded-2xl border border-[var(--glass-hairline)] bg-white/50 px-4 py-3 dark:bg-white/5" data-id="element-123" data-mp="fa7kid6mn8eu">
            <div data-id="element-124" data-mp="1bz6upxqruk9o">
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-id="element-125" data-mp="1bzowtb047spi">Ready to export</p>
              <p className="text-sm font-medium text-foreground" data-id="element-126" data-mp="17zohzp93tfhx">{describeExportScope(options)}</p>
            </div>
            <p className="font-mono text-2xl font-semibold text-foreground" data-id="element-127" data-mp="135tu09mgued1">{selection.length}</p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end" data-id="element-128" data-mp="1voze2tfielpq">
          <Button variant="ghost" className="h-10 rounded-xl" onClick={() => onOpenChange(false)} data-id="element-129" data-mp="1qxr4nxe6j0ck">
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={exporting || blocked} className="h-10 rounded-xl bg-emerald-600 px-5 text-white hover:bg-emerald-700" data-id="element-130" data-mp="29v124uukvsxe">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-131" data-mp="21166qej9dguy" /> : <Download className="mr-2 h-4 w-4" data-id="element-132" data-mp="25315pcdpvi5k" />}
            {exporting ? 'Generating…' : 'Download .xlsx'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
}